import React, { useState, useEffect } from 'react';
import { beginProfile } from '../../utils/renderProfiler';
import { X, Calendar, Clock, MapPin, Navigation, Play, AlertCircle, TrendingUp, Activity, Pause } from 'lucide-react';
import { devLog, measureDevAsync, useDevRenderInstrumentation } from '../../utils/devInstrumentation';

const finishHistoryPlaybackProfile = beginProfile("HistoryPlayback", { stage: "component" });

const TripHistoryPanel = ({ deviceId, deviceName, onClose, map, onPlayTrip }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });

  useDevRenderInstrumentation("HistoryPlayback", () => ({
    deviceId: deviceId || "",
    loading,
    tripsCount: trips.length,
    startDate,
    endDate,
  }));

  useEffect(() => {
    devLog("HistoryPlayback:mount", { deviceId, deviceName });
    fetchTripHistory();
    
    return () => {
      devLog("HistoryPlayback:unmount", { deviceId, deviceName });
    };
  }, [deviceId, startDate, endDate]);

  const fetchTripHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'https://qylmox.in/api';
      const url = `${baseUrl}/history/${deviceId}/report?startDate=${startDate}&endDate=${endDate}`;
      
      const response = await measureDevAsync(
        "HistoryPlayback:fetchTripHistory",
        () =>
          fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-auth-token': token,
              'Content-Type': 'application/json'
            }
          }),
        { deviceId, startDate, endDate }
      );

      if (response.ok) {
        const data = await response.json();
        
        const dailyData = data.dailyData || data.trips || [];
        
        const tripList = dailyData.map(day => ({
          id: `trip_${day.date}`,
          date: day.date,
          startTime: `${day.date}T00:00:00Z`,
          endTime: `${day.date}T23:59:59Z`,
          distance: day.distance * 1000,
          duration: day.movingTime || 0,
          maxSpeed: day.maxSpeed || 0,
          avgSpeed: day.avgSpeed || 0,
          stoppages: day.stoppages || 0,
          route: []
        }));
        
        setTrips(tripList);
        devLog("HistoryPlayback:tripsLoaded", { deviceId, tripsCount: tripList.length });
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch trips:', response.status, errorText);
        setError(`Failed to load trips: ${response.status}`);
        setTrips([]);
      }
    } catch (err) {
      console.error('❌ Error fetching trip history:', err);
      setError(err.message);
      setTrips([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatDistance = (meters) => {
    if (!meters) return '0 km';
    const km = meters / 1000;
    return `${km.toFixed(1)} km`;
  };

  const handlePlayTrip = (trip) => {
    devLog("HistoryPlayback:playTrip", {
      deviceId,
      tripId: trip?.id || "",
      tripDate: trip?.date || "",
    });
    if (onPlayTrip) {
      onPlayTrip(trip);
    }
  };

  const getTotalStats = () => {
    const totalDistance = trips.reduce((sum, trip) => sum + trip.distance, 0);
    const totalDuration = trips.reduce((sum, trip) => sum + trip.duration, 0);
    const maxSpeed = Math.max(...trips.map(t => t.maxSpeed || 0));
    
    return { totalDistance, totalDuration, maxSpeed };
  };

  const stats = getTotalStats();

  const styles = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .trip-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.75);
      z-index: 99998;
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeIn 0.2s ease-out;
    }

    .trip-panel {
      position: relative;
      width: 90%;
      max-width: 900px;
      max-height: 90vh;
      background: white;
      border-radius: 16px;
      box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6);
      z-index: 99999;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      animation: fadeIn 0.3s ease-out;
    }

    .trip-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px 28px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
    }

    .header-icon {
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 8px;
    }

    .close-btn {
      background: rgba(255, 255, 255, 0.2);
      border: none;
      cursor: pointer;
      padding: 10px;
      border-radius: 10px;
      color: white;
      display: flex;
      align-items: center;
      transition: all 0.2s;
    }

    .close-btn:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: scale(1.05);
    }

    .trip-content {
      flex: 1;
      overflow-y: auto;
      padding: 28px;
      background: #f8f9fa;
    }

    .trip-content::-webkit-scrollbar {
      width: 8px;
    }

    .trip-content::-webkit-scrollbar-track {
      background: linear-gradient(180deg, #f8fbff 0%, var(--logo-blue-tint, #e9f6ff) 100%);
    }

    .trip-content::-webkit-scrollbar-thumb {
      background: linear-gradient(180deg, var(--logo-blue-soft, #78cfff) 0%, var(--logo-blue, #1d63c8) 100%);
      border-radius: 4px;
      border: 1px solid rgba(255, 255, 255, 0.65);
    }

    .filter-card {
      background: white;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 24px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      border: 1px solid #e5e7eb;
    }

    .filter-label {
      font-size: 13px;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .date-inputs {
      display: flex;
      gap: 12px;
      align-items: center;
      flex-wrap: wrap;
    }

    .date-input-wrapper {
      flex: 1;
      min-width: 200px;
    }

    .date-input {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid #e5e7eb;
      border-radius: 10px;
      font-size: 14px;
      font-family: inherit;
      transition: all 0.2s;
    }

    .date-input:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
      margin-bottom: 24px;
    }

    .stat-card {
      background: white;
      border-radius: 12px;
      padding: 20px;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      border: 1px solid #e5e7eb;
      transition: all 0.3s;
    }

    .stat-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
    }

    .stat-icon {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 12px;
    }

    .stat-value {
      font-size: 24px;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 4px;
    }

    .stat-label {
      font-size: 13px;
      color: #6b7280;
      font-weight: 500;
    }

    .trip-card {
      background: white;
      border-radius: 12px;
      padding: 24px;
      margin-bottom: 16px;
      border: 2px solid #e5e7eb;
      transition: all 0.3s;
      animation: fadeIn 0.4s ease-out;
    }

    .trip-card:hover {
      border-color: #667eea;
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
      transform: translateY(-2px);
    }

    .trip-date {
      font-size: 18px;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .trip-stats {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      gap: 16px;
      margin-bottom: 16px;
    }

    .trip-stat {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .trip-stat-label {
      font-size: 12px;
      color: #6b7280;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .trip-stat-value {
      font-size: 16px;
      font-weight: 600;
      color: #1f2937;
    }

    .trip-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      background: #fef3c7;
      color: #92400e;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
    }

    .play-btn {
      padding: 12px 28px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 10px;
      cursor: pointer;
      font-size: 15px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.3s;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }

    .play-btn:hover {
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
      transform: translateY(-2px);
    }

    .loading-container {
      text-align: center;
      padding: 80px 20px;
    }

    .spinner {
      width: 60px;
      height: 60px;
      border: 4px solid #e5e7eb;
      border-top-color: #667eea;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 24px;
    }

    .error-container, .empty-container {
      text-align: center;
      padding: 60px 20px;
    }

    .error-icon, .empty-icon {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
    }

    .error-icon {
      background: #fee2e2;
    }

    .empty-icon {
      background: #f3f4f6;
    }

    .retry-btn {
      margin-top: 20px;
      padding: 10px 24px;
      background: #667eea;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      transition: all 0.2s;
    }

    .retry-btn:hover {
      background: #5568d3;
      transform: translateY(-1px);
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="trip-overlay" onClick={onClose}>
        <div className="trip-panel" onClick={(e) => e.stopPropagation()}>
          <div className="trip-header">
            <div>
              <div className="header-icon">
                <Activity size={20} />
              </div>
              <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>
                Trip History
              </h3>
              <p style={{ margin: '6px 0 0', fontSize: '14px', opacity: 0.95 }}>
                {deviceName}
              </p>
            </div>
            <button className="close-btn" onClick={onClose}>
              <X size={22} />
            </button>
          </div>

          <div className="trip-content">
            <div className="filter-card">
              <div className="filter-label">
                <Calendar size={16} />
                Select Date Range
              </div>
              <div className="date-inputs">
                <div className="date-input-wrapper">
                  <input
                    type="date"
                    className="date-input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate}
                  />
                </div>
                <span style={{ color: '#6b7280', fontWeight: 500 }}>to</span>
                <div className="date-input-wrapper">
                  <input
                    type="date"
                    className="date-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
            </div>

            {!loading && !error && trips.length > 0 && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <Navigation size={28} color="white" />
                  </div>
                  <div className="stat-value">{formatDistance(stats.totalDistance)}</div>
                  <div className="stat-label">Total Distance</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                    <Clock size={28} color="white" />
                  </div>
                  <div className="stat-value">{formatDuration(stats.totalDuration)}</div>
                  <div className="stat-label">Total Time</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                    <TrendingUp size={28} color="white" />
                  </div>
                  <div className="stat-value">{Math.round(stats.maxSpeed)} km/h</div>
                  <div className="stat-label">Max Speed</div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <div style={{ fontSize: '16px', color: '#667eea', fontWeight: 500 }}>
                  Loading trips...
                </div>
              </div>
            ) : error ? (
              <div className="error-container">
                <div className="error-icon">
                  <AlertCircle size={40} color="#ef4444" />
                </div>
                <h4 style={{ margin: '0 0 8px', fontSize: '18px', color: '#1f2937' }}>
                  Error Loading Trips
                </h4>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>{error}</p>
                <button className="retry-btn" onClick={fetchTripHistory}>
                  Retry
                </button>
              </div>
            ) : trips.length === 0 ? (
              <div className="empty-container">
                <div className="empty-icon">
                  <MapPin size={40} color="#9ca3af" />
                </div>
                <h4 style={{ margin: '0 0 8px', fontSize: '18px', color: '#1f2937' }}>
                  No Trips Found
                </h4>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                  No trips recorded for this period
                </p>
              </div>
            ) : (
              <div>
                {trips.map((trip, index) => (
                  <div key={trip.id || trip._id || index} className="trip-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div className="trip-date">
                        <Calendar size={20} color="#667eea" />
                        {new Date(trip.startTime).toLocaleDateString('en-IN', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                      <button className="play-btn" onClick={() => handlePlayTrip(trip)}>
                        <Play size={16} />
                        Play Trip
                      </button>
                    </div>

                    <div className="trip-stats">
                      <div className="trip-stat">
                        <div className="trip-stat-label">
                          <Navigation size={14} />
                          Distance
                        </div>
                        <div className="trip-stat-value">{formatDistance(trip.distance)}</div>
                      </div>
                      <div className="trip-stat">
                        <div className="trip-stat-label">
                          <Clock size={14} />
                          Duration
                        </div>
                        <div className="trip-stat-value">{formatDuration(trip.duration)}</div>
                      </div>
                      {trip.maxSpeed > 0 && (
                        <div className="trip-stat">
                          <div className="trip-stat-label">
                            <TrendingUp size={14} />
                            Max Speed
                          </div>
                          <div className="trip-stat-value">{Math.round(trip.maxSpeed)} km/h</div>
                        </div>
                      )}
                      {trip.avgSpeed > 0 && (
                        <div className="trip-stat">
                          <div className="trip-stat-label">
                            <Activity size={14} />
                            Avg Speed
                          </div>
                          <div className="trip-stat-value">{Math.round(trip.avgSpeed)} km/h</div>
                        </div>
                      )}
                    </div>

                    {trip.stoppages > 0 && (
                      <div className="trip-badge">
                        <Pause size={14} />
                        {trip.stoppages} Stoppages
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TripHistoryPanel;
